import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Grid } from 'antd';
import { delay, isEqual, sum } from 'lodash-es';
import { DateTime } from 'luxon';
import { css } from 'styled-components';

import { trackEvent } from 'jf/analytics/Analytics';
import { Prompt, PublicPrompt, PublicStudyClient } from 'jf/api';
import { DevExLoader } from 'jf/components/DevExLoader';
import { ElementObserver } from 'jf/components/ElementObserver';
import { DEFAULT_TEAM_DEMOGRAPHIC_TEXT } from 'jf/pages/studyBuilder/customizeStudy/CustomizableDemographic';
import { DEFAULT_WELCOME_MESSAGE } from 'jf/pages/studyBuilder/customizeStudy/CustomizableIntroduction';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { QuestionCardDemographic } from './question/QuestionCardDemographic';
import { QuestionCardIntro } from './question/QuestionCardIntro';
import { PromptValueType, QuestionCardPrompt } from './question/QuestionCardPrompt';
import { QuestionCardVerbatims, validateVerbatims } from './question/QuestionCardVerbatims';
import { STUDY_PROGRESS_SIDEBAR_WIDTH } from './StudyProgressSidebar';
import { getStudySteps, useStudyState } from './StudyStateContext';

const { useBreakpoint } = Grid;

const TRANSITION_DURATION = '250ms';

const AI_TOOL_FREQUENCY_PROMPT_SLUG = 'ai_tool_frequency';

const styles = {
  questionCards: css`
    transition: transform ${TRANSITION_DURATION} ease;

    .questionCards__card {
      transition: all ${TRANSITION_DURATION} ease;

      &.card--inactive {
        pointer-events: none;
        opacity: 0.25;
        transform: scale(0.9);
        filter: blur(2.5px) grayscale(100%);
      }
    }
  `,
};

const PROMPT_GAP = 48;

export const StudyQuestions: React.FC = (props) => {
  const breakpoint = useBreakpoint();

  const studyState = useStudyState();

  const [initialized, setInitialized] = useState(false);

  const [studyPrompts, setStudyPrompts] = useState<PublicPrompt[]>([]);

  const [questionCardHeights, setQuestionCardHeights] = useState<number[]>([]);

  const { data: studyData, refetch: refetchStudy } = useClientQuery(
    PublicStudyClient.getPublicStudy,
    {
      studyRef: studyState.refs.studyRef,
    }
  );

  // update the study state when the study data is fetched
  // state variable so we can manipulate the prompts for the AI Tool Survey
  useEffect(() => {
    if (studyData) {
      setStudyPrompts(studyData.prompts);
    }
  }, [studyData]);

  const recalculateCardHeights = () => {
    const questionCards = document.getElementById('questionCards');
    setQuestionCardHeights(
      Array.from(questionCards?.children ?? []).map((child) => child.clientHeight)
    );
  };

  useEffect(() => {
    // Delay recalculation to ensure DOM is updated in case of prompt removal
    setTimeout(() => {
      requestAnimationFrame(() => {
        recalculateCardHeights();
      });
    }, 50);
  }, [studyPrompts]);

  // this function is used to filter out tools from the prompts based on the frequency response
  // it is exclusively used for the AI Tool Survey
  const filterToolsFromPrompts = async (value: Record<string, number>) => {
    if (!studyPrompts.length) {
      return;
    }
    const skippedTools: string[] = [];
    Object.keys(value).forEach((key) => {
      // answer "NEVER" is "1"
      const NEVER = 1;
      if (value[key] === NEVER) {
        skippedTools.push(key);
      }
    });
    // refetch the study to get original prompts in case of
    // going back and editing the frequency prompt
    return refetchStudy().then(({ data: newStudy }) => {
      // study should never be undefined here, but typing dictates it can be
      if (newStudy) {
        let prompts = [...newStudy.prompts];
        let newPrompts: PublicPrompt[] = [];
        let skipped: string[] = [];
        prompts.forEach((prompt) => {
          prompt = { ...prompt };
          // if the prompt is the frequency prompt, add it to the new prompts and move on
          if (prompt.parentSlug === AI_TOOL_FREQUENCY_PROMPT_SLUG) {
            newPrompts.push(prompt);
            // otherwise, if the replacement string is in the skipped tools, skip it
          } else if (skippedTools.includes(prompt.replacementString || '')) {
            skipped.push(prompt.slug);
            // otherwise, if the prompt is a matrix, remove the tool from the choice labels
          } else if (prompt.type === Prompt.type.MATRIX_N || prompt.type === Prompt.type.MATRIX_L) {
            prompt.rowLabels = prompt.rowLabels.filter((label) => !skippedTools.includes(label));
            // if the prompt has no choice labels left, skip it
            if (prompt.rowLabels.length === 0) {
              skipped.push(prompt.slug);
              //otherwise, add it to the new prompts
            } else {
              newPrompts.push(prompt);
            }
          } else {
            // fallthrough to add remaining prompts
            newPrompts.push(prompt);
          }
        });

        setStudyPrompts(newPrompts);

        studyState.setRemovedPromptSlugs(skipped);
      }
    });
  };

  useEffect(() => {
    if (!initialized && studyPrompts.length) {
      const aiFrequencyPrompt = studyPrompts.find(
        (prompt) => prompt.parentSlug === AI_TOOL_FREQUENCY_PROMPT_SLUG
      );
      if (aiFrequencyPrompt) {
        const aiToolFrequencyResponse = studyState.savedPromptResponses[aiFrequencyPrompt.slug];
        if (aiToolFrequencyResponse?.responseDict) {
          filterToolsFromPrompts(aiToolFrequencyResponse.responseDict).then(() => {
            setInitialized(true);
          });
        } else {
          setInitialized(true);
        }
        // else is needed for async nature of filterToolsFromPrompts
      } else {
        setInitialized(true);
      }
    }
  }, [studyState.savedPromptResponses, studyPrompts]);

  const { data: studyResponse } = useClientQuery(PublicStudyClient.getStudyResponse, {
    studyResponseRef: studyState.refs.studyResponseRef,
  });
  const suggestedTeamRef = studyResponse?.teamRef;

  const stepIndex = studyState.stepIndex;

  // use question card heights to compute the correct positioning of the active question card
  const activeCardHeight = useMemo(
    () => questionCardHeights[stepIndex],
    [stepIndex, questionCardHeights]
  );
  const activeCardOffset = useMemo(
    () => sum(questionCardHeights?.slice(0, stepIndex)) + stepIndex * PROMPT_GAP,
    [stepIndex, questionCardHeights]
  );

  // track the last time the step was focused
  const [stepFocus, setStepFocus] = useState<DateTime>();
  // track the ms spent focused on the step before being blurred
  const [stepFocusMs, setStepFocusMs] = useState<number>(0);

  const [submitting, setSubmitting] = useState(false);
  // used to pause while we refetch the survey for AI Tool Survey filtering
  const [recalculatingPrompts, setRecalculatingPrompts] = useState(false);

  const onNextAfterRecalcPrompts = useRef(false);

  useEffect(() => {
    if (onNextAfterRecalcPrompts.current) {
      onNextAfterRecalcPrompts.current = false;
      onNext();
    }
  }, [onNextAfterRecalcPrompts.current]);

  useEffect(() => {
    setStepFocus(DateTime.now());
    setStepFocusMs(0);
  }, [studyState.stageIndex, studyState.stepIndex]);

  useEffect(() => {
    const handleBlur = () => {
      setStepFocus((stepFocus) => {
        if (stepFocus) {
          // keep running count of ms spent focused before being blurred
          setStepFocusMs(
            (stepFocusMs) => stepFocusMs + DateTime.now().toMillis() - stepFocus.toMillis()
          );
        }

        // clear stepFocus when window has been blurred (this makes the logic indempotent)
        return undefined;
      });
    };

    const handleFocus = () => {
      // update stepFocus when window has been focused
      setStepFocus(DateTime.now());
    };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const getResponseTimeMs = () => {
    let responseTimeMs = stepFocusMs;

    // there's no reason to believe stepFocus should ever be undefined here, but better safe than sorry!
    if (stepFocus) {
      responseTimeMs += DateTime.now().toMillis() - stepFocus.toMillis();
    }

    return responseTimeMs;
  };

  const onBack = () => {
    studyState.update({ stepIndex: studyState.stepIndex - 1 }); // go to last step
  };

  const onNext = () => {
    if (studyState.stepIndex === lastStepIndex) {
      trackEvent('survey-taker:last-question:submit', {
        surveyRef: studyState.refs.studyRef,
        surveyResponseRef: studyState.refs.studyResponseRef,
      });

      // delay end of survey so user can see progress hit 100%
      setSubmitting(true);
      delay(() => {
        studyState.update({
          savedEndTime: { endTime: DateTime.now().toISO(), saved: false },
          stageIndex: 2,
        });
      }, 1000);
    } else {
      studyState.update({ stepIndex: studyState.stepIndex + 1 }); // go to next step
    }
  };

  const onTeamSubmit = (teamRef: string) => {
    const teamRefUpdated = studyState.savedTeamRef?.teamRef !== teamRef;

    // update teamRef if it has change
    if (teamRefUpdated) {
      trackEvent('survey-taker:demographic-question:answer', {
        surveyRef: studyState.refs.studyRef,
        surveyResponseRef: studyState.refs.studyResponseRef,
        index: studyState.stepIndex,
        text: DEFAULT_TEAM_DEMOGRAPHIC_TEXT,
        suggestedValue: suggestedTeamRef,
        selectedValue: teamRef,
        suggestionAccepted: suggestedTeamRef ? teamRef === suggestedTeamRef : undefined,
      });

      studyState.update({
        savedTeamRef: {
          teamRef,
          saved: false,
        },
      });
    }
  };
  const onPromptSkip = (prompt: PublicPrompt) => {
    const promptResponse = studyState.savedPromptResponses[prompt.slug];
    const currentValue = promptResponse?.absoluteValue ?? promptResponse?.responseText;
    // if previously not scored or scored but now skipped, save prompt
    const promptResponseUpdated = !promptResponse || !!currentValue;
    if (promptResponseUpdated) {
      const responseTimeMs = getResponseTimeMs();

      trackEvent('survey-taker:score-question:question-skipped', {
        surveyRef: studyState.refs.studyRef,
        surveyResponseRef: studyState.refs.studyResponseRef,
        index: studyState.stepIndex,
        prompt: {
          slug: prompt.slug,
        },
      });
      const savedPromptResponses = { ...studyState.savedPromptResponses };
      savedPromptResponses[prompt.slug] = {
        absoluteValue: undefined,
        responseText: undefined,
        responseTimeMs,
        saved: false,
        skipped: true,
      };
      studyState.update({ savedPromptResponses });
    }
  };
  const onPromptSubmit = (prompt: PublicPrompt, value: PromptValueType) => {
    const promptResponse = studyState.savedPromptResponses[prompt.slug];
    const currentValue =
      promptResponse?.absoluteValue ?? promptResponse?.responseText ?? promptResponse?.responseDict;

    const promptResponseUpdated = currentValue !== value;

    if (promptResponseUpdated) {
      const absoluteValue = typeof value === 'number' ? value : undefined;
      const responseText = typeof value === 'string' ? value : undefined;
      const responseDict = typeof value === 'object' ? value : undefined;
      const responseTimeMs = getResponseTimeMs();

      trackEvent('survey-taker:score-question:answer', {
        surveyRef: studyState.refs.studyRef,
        surveyResponseRef: studyState.refs.studyResponseRef,
        index: studyState.stepIndex,
        prompt: {
          slug: prompt.slug,
          polarity: prompt.polarity,
        },
        absoluteValue,
        responseText,
        responseDict,
        responseTimeMs,
      });

      const savedPromptResponses = { ...studyState.savedPromptResponses };
      savedPromptResponses[prompt.slug] = {
        absoluteValue,
        responseText,
        responseDict,
        responseTimeMs,
        saved: false,
      };
      studyState.update({ savedPromptResponses });
    }
  };

  const onVerbatimsSubmit = (verbatims: { [topicSlug: string]: string }) => {
    const verbatimsUpdated = !isEqual(verbatims, studyState.savedVerbatims.verbatims);

    if (verbatimsUpdated) {
      const responseTimeMs = getResponseTimeMs();

      trackEvent('survey-taker:topic-voting:answer', {
        surveyRef: studyState.refs.studyRef,
        surveyResponseRef: studyState.refs.studyResponseRef,
        index: studyState.stepIndex,
        votes: verbatims,
        responseTimeMs,
      });

      studyState.update({
        savedVerbatims: {
          verbatims,
          responseTimeMs,
          saved: false,
        },
      });
    }
  };

  const studySteps = studyData ? getStudySteps({ ...studyData, prompts: studyPrompts }) : [];

  const lastStepIndex = studySteps.length - 1;
  const numberedStepStartIndex = studySteps.findIndex((step) => step.numbered);

  const questionCards: JSX.Element[] = [];

  // create QuestionCard elements based on studySteps
  if (studyData) {
    for (let i = 0; i < studySteps.length; i++) {
      const step = studySteps[i];

      const disabled = i !== stepIndex;
      const nextLabel = i === lastStepIndex ? 'Submit' : 'Next';
      const number = i - numberedStepStartIndex + 1; // ex. "Question 1"

      // Only PROMPT type to match up with FE (spots using useStudyEstimatedTime) and BE (estimated_completion_time_seconds)
      const questionCount = studySteps.filter((step) => {
        return step.type === 'PROMPT';
      }).length;

      switch (step.type) {
        case 'INTRO':
          questionCards.push(
            <QuestionCardIntro
              key={i}
              onNext={() => {
                trackEvent('survey-taker:introduction:next', {
                  surveyRef: studyState.refs.studyRef,
                  surveyResponseRef: studyState.refs.studyResponseRef,
                });
                onNext();
              }}
              questionCount={questionCount}
              title={<strong>{studyData?.name}</strong>}
              message={studyData?.welcomeMessage || DEFAULT_WELCOME_MESSAGE}
              disabled={disabled}
            />
          );
          break;
        case 'DEMOGRAPHIC':
          if (step.typeIndex === 0) {
            questionCards.push(
              <QuestionCardDemographic
                key={i}
                title={DEFAULT_TEAM_DEMOGRAPHIC_TEXT}
                placeholder={'Select team...'}
                options={studyData?.teams.map((team) => ({ value: team.ref, label: team.value }))}
                value={studyState.savedTeamRef?.teamRef ?? suggestedTeamRef}
                onBack={(teamRef) => {
                  teamRef && onTeamSubmit(teamRef);
                  onBack();
                }}
                onNext={(teamRef) => {
                  teamRef && onTeamSubmit(teamRef);
                  onNext();
                }}
                disabled={disabled}
              />
            );
          }
          break;
        case 'PROMPT':
          const currStep = studySteps[stepIndex];
          const currPrompt = studyPrompts[currStep.typeIndex!];
          const prompt = studyPrompts[step.typeIndex!];
          const promptResponse = studyState.savedPromptResponses[prompt.slug];
          let initialResponse: PromptValueType | undefined =
            promptResponse?.absoluteValue ?? promptResponse?.responseText;

          if (prompt.type === Prompt.type.MATRIX_N || prompt.type === Prompt.type.MATRIX_L) {
            initialResponse = promptResponse?.responseDict;
          }

          questionCards.push(
            <QuestionCardPrompt
              key={prompt.slug}
              number={number}
              onBack={(value) => {
                value && onPromptSubmit(prompt, value);
                onBack();
              }}
              onNext={async (value) => {
                // this is a special case for the AI Tool Survey.
                // Other surveys should never hit this block of code
                if (prompt.parentSlug === AI_TOOL_FREQUENCY_PROMPT_SLUG && value) {
                  // puts a loading state on the question card Next button
                  setRecalculatingPrompts(true);
                  await filterToolsFromPrompts(value as Record<string, number>);
                  setRecalculatingPrompts(false);
                  onPromptSubmit(prompt, value);
                  // no need for timeout here because we already pause while we filter
                  // we need to call onNext after recalculating prompts because
                  // the amount of questions and final index update. this ref will call onNext
                  // after the next state recalc
                  onNextAfterRecalcPrompts.current = true;
                } else {
                  value && onPromptSubmit(prompt, value);
                  setTimeout(() => {
                    onNext();
                  }, 200);
                }
              }}
              onSkip={() => {
                onPromptSkip(prompt);
                setTimeout(() => {
                  onNext();
                }, 200);
              }}
              promptRequired={!!prompt.required}
              nextLabel={nextLabel}
              loading={(i === lastStepIndex && submitting) || recalculatingPrompts}
              prompt={prompt}
              tag={prompt.topicLabel}
              initialValue={initialResponse}
              disabled={disabled}
              active={currPrompt && prompt ? currPrompt.slug === prompt.slug : false}
            />
          );
          break;
        case 'VOTING':
          questionCards.push(
            <QuestionCardVerbatims
              key={i}
              number={number}
              onBack={(verbatims) => {
                validateVerbatims(verbatims) && onVerbatimsSubmit(verbatims);
                onBack();
              }}
              onNext={(verbatims) => {
                onVerbatimsSubmit(verbatims);
                onNext();
              }}
              nextLabel={nextLabel}
              loading={i === lastStepIndex && submitting}
              scoredPrompts={studyPrompts
                .filter(
                  (prompt) =>
                    prompt.type === Prompt.type.RATING_5L || prompt.type === Prompt.type.RATING_5N
                )
                .map((prompt) => ({
                  ...prompt,
                  score: studyState.savedPromptResponses[prompt.slug]?.absoluteValue!,
                  skipped: studyState.savedPromptResponses[prompt.slug]?.skipped,
                  topicLabel: prompt.topicLabel,
                }))}
              initialVerbatims={{ ...studyState.savedVerbatims.verbatims }}
              disabled={disabled}
            />
          );
          break;
      }
    }
  }

  return (
    <div
      style={{
        height: activeCardHeight,
        marginLeft: breakpoint.xl ? STUDY_PROGRESS_SIDEBAR_WIDTH / 2 : undefined,
        marginBlock: '5vh',
        width: '100%',
        transition: `height ${TRANSITION_DURATION} ease`,
      }}
    >
      {initialized ? (
        <ElementObserver
          onChange={(_, initial) => {
            if (initial) {
              // set question card heights as a collective after initial render
              recalculateCardHeights();
            }
          }}
        >
          <Flex
            id="questionCards"
            vertical
            gap={PROMPT_GAP}
            css={styles.questionCards}
            style={{ transform: `translateY(-${activeCardOffset}px)` }}
          >
            {questionCards.map((card, index) => (
              <div
                key={card.key}
                className={`questionCards__card ${index !== stepIndex ? 'card--inactive' : ''}`}
              >
                <ElementObserver
                  onChange={(el, initial) => {
                    if (!initial && el.height !== questionCardHeights[index]) {
                      // update new question card heights as needed
                      setQuestionCardHeights((questionCardHeights) => {
                        questionCardHeights[index] = el.height;
                        return [...questionCardHeights];
                      });
                    }
                  }}
                >
                  {card}
                </ElementObserver>
              </div>
            ))}
          </Flex>
        </ElementObserver>
      ) : (
        <DevExLoader />
      )}
    </div>
  );
};
